import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import SignIn from './pages/SignIn';
import About from './pages/About';
import Faq from './pages/Faq';
import Contact from './pages/Contact';
import Legal from './pages/Legal';
import Onboarding from './pages/Onboarding';
import Home from './pages/Home';
import Create from './pages/Create';
import Account from './pages/Account';
import Routine from './pages/Routine';
import GlowupDetail from './pages/GlowupDetail';
import Vault from './pages/Vault';
import VaultEntry from './pages/VaultEntry';
import Membership from './pages/Membership';
import JudgeRoute from './pages/JudgeRoute';
import AuthGate from './components/auth/AuthGate';
import PublicShell from './components/public/PublicShell';
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { ROUTES } from './contracts/routes';

import { trackSiteVisit } from './analytics/events';
import { storeUtmParams } from './analytics/utm';

// /judge -> /feedback, preserving the query string. React Router's <Navigate>
// takes a path and drops anything after the "?", and the one link that most
// needs this redirect is `/judge?unlock=<scan_id>` — the deep link the unlock
// modal, Home and the Vault have all been handing out.
function LegacyJudgeRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${ROUTES.JUDGE}${search}`} replace />;
}


function App() {
  useEffect(() => {
    const utmParams = storeUtmParams();
    const source = utmParams.utm_source || 'direct';
    trackSiteVisit(source);

    const setVhProperty = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVhProperty();
    window.addEventListener('resize', setVhProperty);
    window.addEventListener('orientationchange', setVhProperty);

    return () => {
      window.removeEventListener('resize', setVhProperty);
      window.removeEventListener('orientationchange', setVhProperty);
    };
  }, []);

  return (
    <Router>
      <AuthProvider>
        <UserProvider>
        <Routes>
          {/* Public — Sign-In / Sign-Up is the default surface. All public
              routes nest under PublicShell (layout route), which mounts the
              single fixed bottom tab bar. No AuthGate: these must render for
              signed-out visitors and crawlers. */}
          <Route element={<PublicShell />}>
            <Route path={ROUTES.SPLASH} element={<SignIn />} />
            <Route path={ROUTES.SIGN_IN} element={<SignIn />} />
            <Route path={ROUTES.ABOUT} element={<About />} />
            <Route path={ROUTES.FAQ} element={<Faq />} />
            <Route path={ROUTES.CONTACT} element={<Contact />} />
            <Route
              path={ROUTES.LEGAL}
              element={<Navigate to={ROUTES.LEGAL_TERMS} replace />}
            />
            <Route path={ROUTES.LEGAL_TERMS} element={<Legal doc="tos" />} />
            <Route path={ROUTES.LEGAL_PRIVACY} element={<Legal doc="privacy" />} />
          </Route>

          {/* Mandatory onboarding. AuthGate forces any authenticated user
              without onboarding_complete here, and bounces finished users
              back out to /home. A brand-new user finishing onboarding still
              lands on /create — they have no reading yet, so /home would greet
              them with an empty state. */}
          <Route
            path={ROUTES.ONBOARDING}
            element={<AuthGate><Onboarding /></AuthGate>}
          />

          {/* Auth-gated. AuthGate redirects unauthenticated visitors to
              /sign-in with the original destination preserved in
              location.state.from so SignIn returns them where they were going.

              Order matters: /glowups/new must register before
              /glowups/:glowup_id so React Router treats "new" as a literal. */}
          <Route
            path={ROUTES.HOME}
            element={<AuthGate><Home /></AuthGate>}
          />
          <Route
            path={ROUTES.CREATE}
            element={<AuthGate><Create /></AuthGate>}
          />
          <Route
            path={ROUTES.ACCOUNT}
            element={<AuthGate><Account /></AuthGate>}
          />
          <Route
            path={ROUTES.ROUTINE}
            element={<AuthGate><Routine /></AuthGate>}
          />
          {/* Legacy entry points fold into the unified Create surface; the
              own-glowups list lives in the Vault's GLOWUPS sub-tab. */}
          <Route
            path={ROUTES.ORACLE}
            element={<Navigate to={ROUTES.HOME} replace />}
          />
          <Route
            path={ROUTES.GLOWUPS_NEW}
            element={<Navigate to={`${ROUTES.CREATE}?mode=glowup`} replace />}
          />
          <Route
            path={ROUTES.GLOWUPS}
            element={<Navigate to={`${ROUTES.VAULT}?tab=glowups`} replace />}
          />
          <Route
            path={ROUTES.GLOWUP_DETAIL}
            element={<AuthGate><GlowupDetail /></AuthGate>}
          />
          {/* Explore. The scan deck is primary; the two-photo Glowup
              deck sits behind ?mode=glowup — the same segmented-switch pattern
              /create uses, so no BottomTabBar slot has to be evicted. */}
          <Route
            path={ROUTES.JUDGE}
            element={<AuthGate><JudgeRoute /></AuthGate>}
          />
          {/* /judge -> /feedback. The ONLY reference to JUDGE_LEGACY in the
              app, and it must outlive the rename: the old path is in bookmarks
              and in `?unlock=<scan_id>` links that have already been sent.
              `replace` keeps the dead path out of history, and the search
              string is carried across by hand — <Navigate to="..."> drops the
              query, which would land someone on Explore with their unlock
              context silently gone. */}
          <Route path={ROUTES.JUDGE_LEGACY} element={<LegacyJudgeRedirect />} />

          {/* Vault (Phase 1, Lane A). */}
          <Route
            path={ROUTES.VAULT}
            element={<AuthGate><Vault /></AuthGate>}
          />
          <Route
            path={ROUTES.VAULT_ENTRY}
            element={<AuthGate><VaultEntry /></AuthGate>}
          />

          {/* Membership (Stripe). Deliberately absent from the nav —
              BottomTabBar caps at 4 tabs and is full. Reached from Account,
              the unlock gate, and the Routine paywall.
              SUCCESS/CANCEL are Stripe's redirect targets; SUCCESS reads the
              live entitlement doc rather than trusting the redirect, because a
              query param that grants access is a paywall anyone can type past. */}
          <Route
            path={ROUTES.MEMBERSHIP}
            element={<AuthGate><Membership mode="status" /></AuthGate>}
          />
          <Route
            path={ROUTES.MEMBERSHIP_SUCCESS}
            element={<AuthGate><Membership mode="success" /></AuthGate>}
          />
          <Route
            path={ROUTES.MEMBERSHIP_CANCEL}
            element={<AuthGate><Membership mode="cancel" /></AuthGate>}
          />

          <Route path="*" element={<Navigate to={ROUTES.SIGN_IN} replace />} />
        </Routes>
        </UserProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
